import React, { useState } from "react";
import { Check, Maximize2, X } from "lucide-react";
import { cn } from "../../../utils/classMerger";
import { SlideshowTemplate } from "./slideshowTemplates";
import { Dialog, DialogContent, DialogTitle } from "../../misc/Dialog";

interface SlideshowTemplateCardProps {
    template: SlideshowTemplate;
    selected: boolean;
    onSelect: () => void;
    /** Renders the card dimmed and non-selectable; onSelect still fires so the
     *  caller can explain why (e.g. open the photo-limit notice). */
    disabled?: boolean;
    className?: string;
}

// Mirrors the ZapCap caption-style card (CaptionStyleCard): previews the
// template with a looping muted MP4 and uses the marabot palette (secondary
// gold) for the active state. The expand button opens it in a larger preview.
const SlideshowTemplateCard: React.FC<SlideshowTemplateCardProps> = ({
    template,
    selected,
    onSelect,
    disabled = false,
    className = "",
}) => {
    const [previewOpen, setPreviewOpen] = useState(false);

    return (
        <div className={cn("relative h-full", className)}>
            <button
                type="button"
                onClick={onSelect}
                aria-pressed={selected}
                aria-disabled={disabled}
                className={cn(
                    "flex h-full w-full flex-col rounded-lg overflow-hidden text-left transition-all border-2 p-0",
                    selected
                        ? "border-secondary"
                        : "border-neutral-200 hover:border-neutral-300",
                    disabled &&
                        "opacity-60 cursor-not-allowed hover:border-neutral-200"
                )}
            >
                <div className="bg-primary aspect-video overflow-hidden flex items-center justify-center">
                    {template.preview ? (
                        <video
                            src={template.preview}
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            aria-label={`${template.label} preview`}
                            className="w-full h-full block object-contain"
                        />
                    ) : (
                        <span className="text-xs font-medium text-white/70">
                            Preview coming soon
                        </span>
                    )}
                </div>
                {selected && (
                    <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-secondary text-primary shadow">
                        <Check className="w-4 h-4" />
                    </span>
                )}
                <div
                    className={cn(
                        "flex-1 px-2 py-2 transition-colors",
                        selected ? "bg-lightSecondary" : ""
                    )}
                >
                    <div className="text-primary font-semibold text-sm">
                        {template.label}
                    </div>
                    <div className="text-xs text-neutral-500">
                        {template.description}
                    </div>
                </div>
            </button>

            {/* Expand to a larger preview (sibling of the card button so it's
                not an invalid nested button). Only shown when a preview clip
                exists — templates without one render a blank card. */}
            {template.preview && (
                <>
                    <button
                        type="button"
                        aria-label={`Preview ${template.label} larger`}
                        onClick={() => setPreviewOpen(true)}
                        className="absolute top-2 left-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                        <Maximize2 className="w-3.5 h-3.5" />
                    </button>

                    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                        <DialogContent className="relative max-w-3xl bg-white">
                            <button
                                type="button"
                                onClick={() => setPreviewOpen(false)}
                                aria-label="Close"
                                className="absolute right-4 top-4 text-secondary hover:text-primary focus:outline-none"
                            >
                                <X className="h-6 w-6" />
                            </button>
                            <DialogTitle className="mb-1 text-lg font-semibold text-primary">
                                {template.label}
                            </DialogTitle>
                            <p className="mb-4 text-sm text-neutral-500">
                                {template.description}
                            </p>
                            <div className="bg-primary rounded-lg overflow-hidden">
                                <video
                                    src={template.preview}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    aria-label={`${template.label} preview`}
                                    className="mx-auto block w-full max-h-[70vh] object-contain"
                                />
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </div>
    );
};

export default SlideshowTemplateCard;
